import { Location } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import swal from 'sweetalert2/dist/sweetalert2.js';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth.service';
import { DataService } from '../data/data.service';
import { Organization } from '../data/organization';
import { OrganizationService } from '../organization.service';
import { RedirectService } from '../redirect.service';
import { OrganizationDetail } from '../data/organization-detail';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-base',
  templateUrl: './base.component.html',
  styleUrls: ['./base.component.css']
})
export class BaseComponent implements OnInit {

  editObj: object;
  origEditObj: object;
  deleteObj: object;
  invalidFormError = '';
  adding = false;
  saving = false;
  sendingWelcomeEmail = false;
  dateFormat = environment.DATE_FORMAT;
  allListItems: any[];
  listItems$: Observable<any[]>;
  private searchTerms = new Subject<string>();
  private searchInitialized = false;
  searching = false;
  noMatches = false;

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    if (!this.origEditObj || !this.editObj) {
      return true;
    }
    if (JSON.stringify(this.editObj) !== JSON.stringify(this.origEditObj)) {
      return false;
    }
    return true;
  }

  get currentUserID(): number
  {
    return this.dataService.currentUserID;
  }

  constructor(
    protected route: ActivatedRoute,
    protected authService: AuthService,
    protected organizationService: OrganizationService,
    protected redirectService: RedirectService,
    protected router: Router,
    protected dataService: DataService,
    protected location: Location
  ) {
  }

  ngOnInit() {
    // this.initializeSearch();
  }

  cancelEdit(obj: any) {
    if (obj) {
      Object.keys(obj).forEach(k => delete obj[k]);
      Object.assign(obj, this.origEditObj);
    }
    this.editObj = null;
    this.origEditObj = null;
  }

  cancelEditObj(event: Event, obj: any): void {
    this.cancelEdit(obj);
    event.stopPropagation();
  }

  getOrgRouterLink(org: Organization): string {
    return Organization.getLink(org);
  }

  getOrgRouterQueryParams(org: Organization, tab: string = null): any {
    return Organization.getLinkQueryParams(org, tab);
  }

  goBack(): void {
    this.location.back();
  }

  goToRoute(obj: any, route: string, params: any): void {
    if (this.editObj !== obj && this.deleteObj !== obj) {
      this.router.navigate([route], { queryParams: params ? params : {} });
    }
  }

  goToOrg(org: Organization, tab: string = null): void {
    this.goToRoute(
      org,
      Organization.getLink(org),
      Organization.getLinkQueryParams(org, tab),
    );
  }

  handleSaveSuccess(): void {
    this.editObj = null;
    this.origEditObj = null;
    this.saving = false;
  }

  handleSaveError(): void {
    this.saving = false;
  }

  initializeSearch(): void {
    this.searching = true;
    this.listItems$ = this.searchTerms.pipe(
      // wait 300ms after each keystroke before considering the term
      debounceTime(300),
      // ignore new term if same as previous term
      distinctUntilChanged(),
      // switch to new search observable each time the term changes
      switchMap((term: string) => {
        return this.performSearch(encodeURIComponent(term)).pipe(
          // delay(2000),
          tap((response: object[]) => {
            this.searching = false;
            this.noMatches = !response.length;
          })
        );
      }),
    );
  }

  performSearch(term): Observable<any> {
    // override
    return of([]);
  }

  printPage(header: string): void {
    let oldTitle = document.title;
    if (header) document.title = header;
    window.print();
    document.title = oldTitle;
  }

  // Push a search term into the observable stream.
  queueSearchTerm(term: string): void {
    this.noMatches = false;
    term = term.trim();
    if (term) {
      if (!this.searchInitialized) {
        this.initializeSearch();
        this.searchInitialized = true;
        // this fixes a bug where sometimes the first term doesn't get registered after search init, not sure why
        setTimeout(() => this.searchTerms.next(term), 0);
      }
      else {
        this.searching = true;
        this.searchTerms.next(term);
      }
    }
    else {
      this.searching = false;
      this.searchInitialized = false;
      this.listItems$ = of(this.allListItems);
    }
  }

  removeListItem(item): void {
    this.allListItems.splice(this.allListItems.indexOf(item), 1);
  }


  startEditObj(event: Event, obj: any): void {
    this.startEdit(obj);
    event.stopPropagation();
  }

  startEdit(obj: any): void {
    if (this.editObj) {
      this.cancelEditObj(event, this.editObj);
    }
    this.editObj = obj;
    // deep copy the object to copy sub objects like OrganizationDetail.Administrator
    this.origEditObj = JSON.parse(JSON.stringify(obj));
  }

  startSaveObj(event: Event): void {
    this.saving = true;
    event.stopPropagation();
  }

  validateDate(dateStr): boolean {
    return dateStr ? moment(dateStr).isValid() : false;
  }

  getOrganizationType(org: Organization) {
    return Organization.getType(org);
  }

  getTopTeacher(org: OrganizationDetail): string {
    var teachers = org.Administrators.filter(a => a.RoleCode === 'TEACH');
    var name = teachers && teachers.length > 0 ? teachers[0].AccountName : "";
    if (teachers.length > 1) name += "*";
    return name;
  }
}
